import React from "react";
import {
  Box,
  Button,
  CommandItem,
  EmptyState,
  Icon,
  Kbd,
  Skeleton,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  toastManager,
} from "@salestrak/ui";
import { IS_MAC } from "#app/lib/platform";
import type { EntityData } from "#app/lib/entity-visuals";
import { copyToClipboard } from "#app/lib/utils/copy-to-clipboard";
import { EntityTag } from "#app/components/entity";
import type { Omittable } from "@salestrak/ui";

export const NEW_TAB_SHORTCUT = IS_MAC ? "⌘ Enter" : "Ctrl Enter";
export const COPY_SHORTCUT = IS_MAC ? "⌘ C" : "Ctrl C";

// One copy path for the row button and the ⌘/Ctrl+C hotkey — same toast
// either way.
export function copyUrl(url: string) {
  void copyToClipboard(url).then((ok) => {
    toastManager.add(
      ok
        ? { title: "URL copied", type: "success" }
        : { title: "Failed to copy URL", type: "error" },
    );
  });
}

// Every row's leading visual — entity tile/avatar, address pin, command icon
// — occupies this square, so names start on one vertical rail and the glyph
// column matches the 28px action buttons.
export const ROW_VISUAL_BOX = 28;

// Keep the search input focused: a mousedown's default would move focus to
// the action button and break arrow-key navigation (Base UI binds it to the
// input).
function keepInputFocus(e: React.MouseEvent) {
  e.preventDefault();
}

/**
 * The highlighted row's trailing action cluster. Every row gets the one
 * THEMED action — the primary ↵ "open", mirroring exactly what Enter does —
 * and rows with a real URL (projects, workflows) add the quiet ghost pair in
 * front of it: open in new tab and copy link. Copy/new-tab leave the dialog
 * open on purpose (open-several-in-tabs is the power flow); only the row's
 * own select closes it.
 */
export function RowActions({
  url,
  onOpen,
  openLabel = "Open in SalesTrak",
}: Omittable<{
  /** Absolute URL for the new-tab/copy pair; omit for rows that open an
   *  overlay (members) or run a command — they get ↵ only. */
  url?: string;
  onOpen: () => void;
  /** What the ↵ action does, named for its aria-label and tooltip. The
   *  default is the open verb every entity row shares; an address row that
   *  creates a project says so here — the row itself carries no label. */
  openLabel?: string;
}>) {
  return (
    <Box direction="row" align="center" gap="2xs">
      {url ? (
        <>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  size="icon-sm"
                  shape="square"
                  variant="outline"
                  aria-label="Open in new tab"
                  onMouseDown={keepInputFocus}
                  onClick={(e) => {
                    e.stopPropagation();
                    window.open(url, "_blank", "noopener,noreferrer");
                  }}
                />
              }
            >
              <Icon name="external-link" size="sm" color="muted" />
            </TooltipTrigger>
            <TooltipPopup>
              <Box direction="row" align="center" gap="sm">
                <Text size="xs">Open in new tab</Text>
                <Kbd>{NEW_TAB_SHORTCUT}</Kbd>
              </Box>
            </TooltipPopup>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger
              render={
                <Button
                  size="icon-sm"
                  shape="square"
                  variant="outline"
                  aria-label="Copy URL"
                  onMouseDown={keepInputFocus}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyUrl(url);
                  }}
                />
              }
            >
              <Icon name="link" size="sm" color="muted" />
            </TooltipTrigger>
            <TooltipPopup>
              <Box direction="row" align="center" gap="sm">
                <Text size="xs">Copy URL</Text>
                <Kbd>{COPY_SHORTCUT}</Kbd>
              </Box>
            </TooltipPopup>
          </Tooltip>
        </>
      ) : null}
      <Tooltip>
        <TooltipTrigger
          render={
            <Button
              size="icon-sm"
              shape="square"
              aria-label={openLabel}
              onMouseDown={keepInputFocus}
              onClick={(e) => {
                e.stopPropagation();
                onOpen();
              }}
            />
          }
        >
          <Icon name="corner-down-left" size="sm" />
        </TooltipTrigger>
        <TooltipPopup>
          <Box direction="row" align="center" gap="sm">
            <Text size="xs">{openLabel}</Text>
            <Kbd>Enter</Kbd>
          </Box>
        </TooltipPopup>
      </Tooltip>
    </Box>
  );
}

/**
 * Rich empty state (reference grammar): a quiet glyph well, a title, one line
 * of supporting subtext, and — when kind filters are active — a Clear
 * filters action, since the filters are usually why nothing matched.
 */
export function SearchEmptyState({
  title,
  subtext,
  onClearFilters,
}: Omittable<{
  title: string;
  subtext: string;
  onClearFilters?: () => void;
}>) {
  return (
    <EmptyState
      size="sm"
      media="circle"
      icon="search-x"
      title={title}
      description={subtext}
      actions={
        onClearFilters ? (
          <Button
            size="sm"
            variant="outline"
            onMouseDown={keepInputFocus}
            onClick={onClearFilters}
          >
            Clear filters
          </Button>
        ) : undefined
      }
    />
  );
}

/** Placeholder rows the cold search window lays. */
const SKELETON_ROW_COUNT = 5;

/**
 * Placeholder rows for the cold search window — a typed query is fetching and
 * nothing is on screen yet (once any section lands, real rows render and later
 * sections stream in without placeholders). The heading is real text: the
 * group is always titled "Results", so only the rows are unknown. Each row
 * mirrors ResultRow's metrics — the 28px visual rail (a 16px bar, the size of
 * every mark that lands in it) and the item's 8px inset / 40px total height —
 * so results landing swap content without any reflow.
 */
export function SearchResultsSkeleton() {
  return (
    <Box direction="column">
      <Box height={28} paddingX="sm" align="start" justify="center" direction="column">
        <Text size="xs" weight="medium" variant="faint">
          Results
        </Text>
      </Box>
      {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
        <Box
          key={index}
          direction="row"
          align="center"
          gap="sm"
          paddingX="sm"
          height={40}
        >
          <Box
            width={ROW_VISUAL_BOX}
            height={ROW_VISUAL_BOX}
            align="center"
            justify="center"
            shrink={false}
          >
            <Skeleton width={16} height={16} />
          </Box>
          <Skeleton height={12} vary={index} width={208} />
        </Box>
      ))}
    </Box>
  );
}

/**
 * A single result row. `highlighted` is the Command root's highlight — Base UI
 * moves it on both arrow keys and pointer hover, so one flag swaps in the
 * trailing `actions` cluster (RowActions).
 * `meta` is the muted inline context after the name (`in <location> · 3d ago`).
 */
export function ResultRow({
  value,
  highlighted,
  entity,
  description,
  meta,
  actions,
  onSelect,
}: Omittable<{
  value: string;
  highlighted: boolean;
  entity: EntityData;
  description?: string;
  meta?: string;
  actions?: React.ReactNode;
  onSelect: () => void;
}>) {
  return (
    <CommandItem value={value} onClick={onSelect}>
      {/* minHeight pins the row to the action cluster's height so swapping
          the buttons in on highlight never reflows the list under the
          pointer. */}
      <Box
        direction="row"
        align="center"
        gap="sm"
        fill="width"
        justify="between"
        minHeight={28}
      >
        <Box shrink direction="row" align="center" minWidth={0}>
          <EntityTag
            entity={entity}
            // sm = the command rows' 14px/sm-glyph scale, so every search row
            // reads at one type size.
            size="sm"
            weight="regular"
            padded
            interactive={false}
            ghost={false}
            // The 28px visual rail. Every leading mark in the list — project
            // glyph, address pin, command icon — is a 16px glyph centered in
            // it, so member circles and team tiles pin to the 16px avatar rung
            // too: one mark size down the whole column, whatever the kind.
            visualBoxSize={ROW_VISUAL_BOX}
            avatarSize="2xs"
            description={description}
            meta={meta}
          />
        </Box>
        {highlighted ? (actions ?? null) : null}
      </Box>
    </CommandItem>
  );
}

/**
 * A geocoded address result row (not an entity — a Google Places suggestion), so
 * it renders a map-pin + the address text rather than an EntityTag. Selecting it
 * resolves the place and opens the map there; where the viewer can create, it
 * also opens a project draft at that address. The row carries no label for
 * that — a label seated beside the ↵ button reads as a second control. The
 * verb lives where every row states what Enter does: the ↵ tooltip and the
 * footer's context.
 */
export function AddressRow({
  value,
  highlighted,
  description,
  actionLabel,
  onSelect,
}: {
  value: string;
  highlighted: boolean;
  description: string;
  /** The ↵ action's name — ADDRESS_ACTION_LABEL for the viewer's case. */
  actionLabel: string;
  onSelect: () => void;
}) {
  return (
    // An address row is the ONLY row that came from Google Places — a live,
    // paid, non-deterministic lookup. The attribute names it so a test can
    // prove a scoped search never fired one; the results list is a single
    // "Results" group, so there is no heading to read the kind off.
    <CommandItem value={value} onClick={onSelect} data-search-kind="address">
      {/* Same min-height pin as ResultRow: the highlight's action cluster
          must not grow the row. */}
      <Box
        direction="row"
        align="center"
        gap="sm"
        fill="width"
        justify="between"
        minHeight={28}
      >
        <Box shrink direction="row" align="center" gap="sm" minWidth={0}>
          {/* Same 28px visual rail as the entity rows' tiles. */}
          <Box
            width={ROW_VISUAL_BOX}
            height={ROW_VISUAL_BOX}
            align="center"
            justify="center"
            shrink={false}
          >
            <Icon name="map-pin" size="sm" color="muted" />
          </Box>
          <Text size="sm" truncate={1}>
            {description}
          </Text>
        </Box>
        {highlighted ? (
          <RowActions onOpen={onSelect} openLabel={actionLabel} />
        ) : null}
      </Box>
    </CommandItem>
  );
}
